/**
 * useLiveKeys - Custom hook for live key press visualization
 * Subscribes to serial data, parses key events, tracks pressed keys
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getSerialConnection } from '@/lib/serialConnection';
import { parseKeyEvent } from '@/lib/keyEventParser';

export interface LiveKeyState {
  velocity: number;
  timestamp: number;
}

const STALE_KEY_TIMEOUT_MS = 2000;
const CLEANUP_INTERVAL_MS = 500;

export function useLiveKeys(enabled: boolean): Map<string, LiveKeyState> {
  const [pressedKeys, setPressedKeys] = useState<Map<string, LiveKeyState>>(() => new Map());
  const lineBufferRef = useRef('');
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const processLine = useCallback((line: string) => {
    const event = parseKeyEvent(line);
    if (!event) return;

    const key = `${event.q},${event.r}`;
    setPressedKeys((prev) => {
      const next = new Map(prev);
      if (event.type === 'press') {
        next.set(key, { velocity: event.velocity ?? 100, timestamp: Date.now() });
      } else if (event.type === 'release') {
        next.delete(key);
      } else if (event.type === 'aftertouch') {
        const existing = next.get(key);
        if (existing) {
          next.set(key, { ...existing, timestamp: Date.now() });
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setPressedKeys(new Map());
      return;
    }

    const conn = getSerialConnection();

    // Subscribe to raw serial data
    const unsubData = conn.onData((data) => {
      if (!enabledRef.current) return;
      lineBufferRef.current += data;

      // Process complete lines
      const lines = lineBufferRef.current.split('\n');
      // Keep the last incomplete line in the buffer
      lineBufferRef.current = lines.pop() ?? '';
      for (const line of lines) {
        processLine(line.trim());
      }
    });

    // Send "keys on" command after connecting
    const unsubState = conn.onStateChange((state) => {
      if (state === 'connected' && enabledRef.current) {
        setTimeout(() => {
          conn.send('keys on\r\n').catch(() => {});
        }, 500);
      }
      if (state === 'disconnected') {
        setPressedKeys(new Map());
      }
    });

    // If already connected, enable key monitoring
    if (conn.isConnected()) {
      conn.send('keys on\r\n').catch(() => {});
    }

    // Cleanup stale keys (handles missed release events)
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setPressedKeys((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [key, state] of next) {
          if (now - state.timestamp > STALE_KEY_TIMEOUT_MS) {
            next.delete(key);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, CLEANUP_INTERVAL_MS);

    return () => {
      unsubData();
      unsubState();
      clearInterval(cleanupInterval);
      lineBufferRef.current = '';
      // Send "keys off" when disabling
      if (conn.isConnected()) {
        conn.send('keys off\r\n').catch(() => {});
      }
    };
  }, [enabled, processLine]);

  return pressedKeys;
}
